import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Pencil, Trash2, X, Loader2, Search } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

function ConcernForm({ initial, products, onClose, onSaved }) {
  const [name, setName] = useState(initial?.name || "");
  const [selected, setSelected] = useState(initial?.product_ids || []);
  const [dosages, setDosages] = useState(initial?.dosages || {});
  const [saving, setSaving] = useState(false);
  const [q, setQ] = useState("");

  const toggle = (p) => {
    setSelected((prev) => {
      if (prev.includes(p.id)) return prev.filter((x) => x !== p.id);
      setDosages((d) => ({ ...d, [p.id]: d[p.id] || p.dosage || "" }));
      return [...prev, p.id];
    });
  };

  const query = q.trim().toLowerCase();
  const visible = query
    ? products.filter((p) => p.name.toLowerCase().includes(query) || (p.category || "").toLowerCase().includes(query))
    : products;
  // Keep selected products always visible even when searching
  const ordered = [...visible].sort((a, b) => (selected.includes(b.id) ? 1 : 0) - (selected.includes(a.id) ? 1 : 0));

  const save = async (e) => {
    e.preventDefault();
    if (!name.trim()) { toast.error("Enter a concern name"); return; }
    setSaving(true);
    try {
      const payload = { name: name.trim(), product_ids: selected, dosages };
      if (initial?.id) await api.updateDisease(initial.id, payload);
      else await api.createDisease(payload);
      toast.success("Concern saved");
      onSaved();
    } catch (err) { toast.error(err.message); } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center p-4 overflow-y-auto">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-paper rounded-2xl w-full max-w-2xl my-8 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-3xl text-ink">{initial?.id ? "Edit" : "Add"} Concern</h2>
          <button onClick={onClose} className="p-1 text-muted2"><X size={22} /></button>
        </div>
        <form onSubmit={save} className="space-y-4">
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Concern name (e.g. Immunity, Diabetes)" className="w-full h-11 rounded-lg border border-line px-3 text-sm focus:outline-none focus:border-brand" data-testid="concern-name-input" />
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-semibold text-muted2 uppercase tracking-wide">Select products &amp; set dosage</p>
              <span className="text-xs text-brand font-semibold">{selected.length} selected</span>
            </div>
            <div className="relative mb-2">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted2" size={16} />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search products to attach…" className="w-full h-10 rounded-lg border border-line pl-9 pr-3 text-sm focus:outline-none focus:border-brand" data-testid="concern-product-search" />
            </div>
            <div className="space-y-2 max-h-80 overflow-y-auto pr-1">
              {ordered.length === 0 && <p className="text-sm text-muted2 py-4 text-center" data-testid="concern-product-empty">No products match "{q}"</p>}
              {ordered.map((p) => {
                const on = selected.includes(p.id);
                return (
                  <div key={p.id} className={`rounded-lg border p-2.5 ${on ? "border-brand bg-mist" : "border-line"}`}>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" checked={on} onChange={() => toggle(p)} data-testid={`concern-product-${p.id}`} />
                      <span className="text-sm text-ink flex-1">{p.name}</span>
                      <span className="text-xs text-muted2">{p.category}</span>
                    </label>
                    {on && (
                      <input value={dosages[p.id] || ""} onChange={(e) => setDosages((d) => ({ ...d, [p.id]: e.target.value }))} placeholder="Recommended dosage" className="w-full h-9 rounded border border-line px-2 text-xs mt-2 focus:outline-none focus:border-brand" />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
          <div className="flex gap-3">
            <button type="button" onClick={onClose} className="flex-1 h-12 rounded-full border border-line font-semibold">Cancel</button>
            <button disabled={saving} className="flex-1 h-12 rounded-full bg-brand text-white font-semibold flex items-center justify-center gap-2 disabled:opacity-60" data-testid="concern-save">{saving ? <Loader2 className="animate-spin" size={18} /> : "Save concern"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function ConcernsAdmin() {
  const qc = useQueryClient();
  const { data: concerns = [] } = useQuery({ queryKey: ["diseases"], queryFn: () => api.listDiseases() });
  const { data: products = [] } = useQuery({ queryKey: ["products", "All"], queryFn: () => api.listProducts("All") });
  const [editing, setEditing] = useState(null);

  const remove = async (id) => {
    if (!window.confirm("Delete this concern?")) return;
    try { await api.deleteDisease(id); toast.success("Deleted"); qc.invalidateQueries({ queryKey: ["diseases"] }); } catch (err) { toast.error(err.message); }
  };
  const saved = () => { setEditing(null); qc.invalidateQueries({ queryKey: ["diseases"] }); };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-display text-2xl text-ink">Shop by Concern ({concerns.length})</h2>
        <button onClick={() => setEditing({})} className="h-10 px-4 rounded-full bg-brand text-white font-semibold text-sm flex items-center gap-1.5" data-testid="add-concern-button"><Plus size={16} /> Add concern</button>
      </div>
      <div className="space-y-2">
        {concerns.map((c) => (
          <div key={c.id} className="flex items-center gap-3 rounded-xl border border-line p-3" data-testid={`admin-concern-${c.id}`}>
            <div className="flex-1">
              <p className="font-medium text-ink text-sm">{c.name}</p>
              <p className="text-xs text-muted2">{c.product_count} products</p>
            </div>
            <button onClick={() => setEditing(c)} className="p-2 text-muted2 hover:text-brand"><Pencil size={16} /></button>
            <button onClick={() => remove(c.id)} className="p-2 text-muted2 hover:text-err"><Trash2 size={16} /></button>
          </div>
        ))}
      </div>
      {editing && <ConcernForm initial={editing} products={products} onClose={() => setEditing(null)} onSaved={saved} />}
    </div>
  );
}
